import { AxiosResponse } from 'axios'
import { Pagination } from '@/domain/entities/Pagination'
import { Utils } from '@/data/infrastructures/misc/Utils'
import { BaseResponseMapper } from './BaseMapper'
import {
  Pickup,
  PickupAccount,
  PickupDetail,
  PickupHistory,
  PickupInfo,
  Pickups,
  NearbyCourier,
  CourierPickupDetail,
  PickupDetailSnapshot,
  Couriers,
  PickupSummary,
  PickupForceDetail,
  PickupAdditionalTimes,
  PickupAdditionalTime,
  PickupCRROTWs,
  PickupCRROTWSummary,
  PickupCRROTW,
  PickupCRROTWV2Agent,
  PickupCRROTWV2Courier,
  PickupCRROTWV2Driver,
  PickupCRROTWV2DurationBuckets,
  PickupCRROTWV2Group,
  PickupCRROTWV2List,
  PickupCRROTWV2ShipmentDetail,
  PickupCRROTWV2Summary,
  PickupCRROTWV2BucketKey,
  DashboardPickupTransferOutcome,
  PickupCRRQUEs,
  PickupCRRQUE,
  PickupCRRQUECustomer,
  PickupCRRQUEPOS,
  PickupCRRQUEPickup,
  PickupCRRQUESummary,
  PickupCRRNFDSummary,
  PickupNfdGroup,
  PickupNfdLastCourier,
  PickupNfdDetailShipment,
  PickupNfdList,
  PickupNfdShipperHashDetail,
  PickupNfdSummary,
  PickupShipperHashOutcome,
  PickupCorporates,
  PickupCorporate,
  PickupCorporateSummary,
  AssignPickupBulkHistoryList,
  AssignPickupBulkHistoryItem,
  AssignPickupBulkHistoryPagination,
  AdminContactShow,
  PickupCorporatesV2,
  PickupCorporateV2,
  PickupCorporateNfdDeadlineSummary,
  PickupCorporatBuckets,
  PickupHistoryActor
} from '@/domain/entities/Pickup'
import {
  PickupDetailSnapshotResponse,
  CouriersResponse,
} from '@/data/persistences/responses/contracts/PickupResponse'
import {
  PickupAccountResponse,
  PickupDetailReponse,
  PickupHistoryResponse,
  PickupResponse,
} from '@/data/responses/contracts/PickupResponse'
import { Report } from '@/domain/entities/Main'

export class PickupMapper extends BaseResponseMapper {
  public convertPickupsFromApi(result: AxiosResponse): Pickups {
    const {
      data: { data, pagination },
    } = result
    return new Pickups(
      Utils.toInstance(new Pagination(), pagination),
      data.map(
        (
          pickup: Record<
            string,
            string | number | boolean | Record<string, string | number>
          >
        ) => {
          const account = <Record<string, string | number>>pickup.account
          const pickupInfo = <Record<string, string | number>>pickup.pickup
          const pickupHistory = <Record<string, string | number>>pickup.history
          const courierPickupDetail = <Record<string, string | number>>(
            (pickup.courier_pickup_detail || pickup.courier_detail)
          )

          return new Pickup(
            <string>pickup.shipment_id,
            <string>pickup.group_id,
            <string>pickup.shipment_type,
            new PickupAccount(
              Number(account.account_id),
              <string>account.account_role
            ),
            pickupInfo
              ? new PickupInfo(
                  <string>pickupInfo.fullname,
                  <string>pickupInfo.address,
                  <string>pickupInfo.phone_number,
                  Number(pickupInfo.latitude),
                  Number(pickupInfo.longitude),
                  <string>pickupInfo.note,
                  <string>pickupInfo.country,
                  <string>pickupInfo.province,
                  <string>pickupInfo.district,
                  <string>pickupInfo.sub_district,
                  <string>pickupInfo.village
                )
              : null,
            <boolean>pickup.is_onepack,
            <boolean>pickup.is_dedicated,
            Number(pickup.quantity),
            <string>pickup.created_at,
            <string>pickup.expired_at,
            new PickupHistory(
              Number(pickupHistory.history_id),
              <string>pickupHistory.history_type,
              <string>pickupHistory.status_id,
              <string>pickupHistory.status,
              <string>pickupHistory.location,
              <string>pickupHistory.city,
              Number(pickupHistory.actor_id),
              <string>pickupHistory.actor_role,
              <string>pickupHistory.actor_name,
              <string>pickupHistory.reason_id,
              <string>pickupHistory.reason,
              <string>pickupHistory.relation_id,
              <string>pickupHistory.relation,
              <string>pickupHistory.person_name,
              <string>pickupHistory.note,
              <string>pickupHistory.meta,
              <string>pickupHistory.created_at,
              <number>pickupHistory.total_nfd
            ),
            <string>pickup.flag,
            <string>pickup.do_origin_package_id,
            <string>pickup.booking_id,
            courierPickupDetail
              ? new CourierPickupDetail(
                  Number(courierPickupDetail.courier_id),
                  <string>(
                    (courierPickupDetail.courier_name ||
                      courierPickupDetail.fullname)
                  ),
                  <string>courierPickupDetail.courier_type_valid,
                  <string>courierPickupDetail.origin,
                  <string>courierPickupDetail.partner_name,
                  <string>courierPickupDetail.partner_type,
                  <string>courierPickupDetail.phone_number,
                  <string>courierPickupDetail.vehicle_plate,
                  <string>courierPickupDetail.vehicle_type
                )
              : undefined,
            <string>pickup.product_type,
            <string>pickup.client_id,
            <string>pickup.client_name,
            <boolean>pickup.is_priority || false,
            <number>pickup.pickup_booster_amount || 0,
            <string>pickup.execution_platform || ''
          )
        }
      )
    )
  }

  public convertPickupFromApi(result: AxiosResponse): PickupDetail {
    const { data } = result

    const account = <Record<string, string | number>>data.account
    const pickupInfo = <Record<string, string | number>>data.pickup
    const pickupHistories = <Record<string, any>[]>data.histories || []

    return new PickupDetail(
      <string>data.shipment_id,
      <string>data.group_id,
      <string>data.shipment_type,
      new PickupAccount(
        Number(account.account_id),
        <string>account.account_role
      ),
      new PickupInfo(
        <string>pickupInfo?.fullname,
        <string>pickupInfo?.address,
        <string>pickupInfo?.phone_number,
        Number(pickupInfo?.latitude),
        Number(pickupInfo?.longitude),
        <string>pickupInfo?.note,
        <string>pickupInfo?.country,
        <string>pickupInfo?.province,
        <string>pickupInfo?.district,
        <string>pickupInfo?.sub_district,
        <string>pickupInfo?.village
      ),
      <boolean>data.is_onepack,
      Number(data.quantity),
      <string>data.created_at,
      <string>data.expired_at,
      <string>data.invoice_id,
      <string>data.booking_id,
      pickupHistories.map(
        pickupHistory =>
          new PickupHistory(
            Number(pickupHistory.history_id),
            <string>pickupHistory.history_type,
            <string>pickupHistory.status_id,
            <string>pickupHistory.status,
            <string>pickupHistory.location,
            <string>pickupHistory.city,
            Number(pickupHistory.actor_id),
            <string>pickupHistory.actor_role,
            <string>pickupHistory.actor_name,
            <string>pickupHistory.reason_id,
            <string>pickupHistory.reason,
            <string>pickupHistory.relation_id,
            <string>pickupHistory.relation,
            <string>pickupHistory.person_name,
            <string>pickupHistory.note,
            <string>pickupHistory.meta,
            <string>pickupHistory.created_at,
            <number>pickupHistory.total_nfd,
            Array.isArray(pickupHistory.actors)
              ? (pickupHistory.actors as Record<string, any>[]).map(actor =>
                  new PickupHistoryActor(
                    Number(actor.actor_id),
                    <string>actor.actor_role,
                    <string>actor.actor_name
                  )
                )
              : []

          )
      ),
      <string>data.flag,
      undefined,
      <string[]>data.shipment_group_ids,
      <string[]>data.product_type,
      <number>data.weight,
      <string>data.client_id,
      <string>data.client_name,
      <string>data.vehicle_expectation,
      <string>data.pickup_date_expectation,
      <string>data.pickup_time_expectation,
      <number>data.estimate_koli,
      <number>data.estimasi_tonase,
      <number>data.total_nfd,
      <boolean>data.is_priority || false,
      <number>data.pickup_booster_amount || 0,
      <string>data.execution_platform || '',
      <string>data.pickup_date_estimation,
      <string>data.pickup_start_time,
      <string>data.pickup_end_time
    )
  }

  public convertPickupBulkFromApi(result: AxiosResponse): PickupDetail[] {
    const {
      data: { data },
    } = result

    return data.map((item: PickupDetailReponse) => {
      const account: PickupAccountResponse = item.account
      const pickupInfo: PickupResponse = item.pickup
      const pickupHistories: Array<PickupHistoryResponse> = item.histories || []

      return new PickupDetail(
        item.shipment_id,
        item.group_id,
        item.shipment_type,
        new PickupAccount(
          Number(account.account_id),
          String(account.account_role)
        ),
        pickupInfo
          ? new PickupInfo(
              pickupInfo.fullname,
              pickupInfo.address,
              pickupInfo.phone_number,
              Number(pickupInfo.latitude),
              Number(pickupInfo.longitude),
              pickupInfo.note,
              pickupInfo.country,
              pickupInfo.province,
              pickupInfo.district,
              pickupInfo.sub_district,
              pickupInfo.village
            )
          : undefined,
        item.is_onepack as boolean,
        Number(item.quantity),
        String(item.created_at),
        String(item.expired_at),
        item.invoice_id,
        item.booking_id,
        pickupHistories.map(
          pickupHistory =>
            new PickupHistory(
              Number(pickupHistory.history_id),
              pickupHistory.history_type,
              pickupHistory.status_id,
              pickupHistory.status,
              pickupHistory.location,
              pickupHistory.city,
              Number(pickupHistory.actor_id),
              pickupHistory.actor_role,
              pickupHistory.actor_name,
              pickupHistory.reason_id,
              pickupHistory.reason,
              pickupHistory.relation_id,
              pickupHistory.relation,
              pickupHistory.person_name,
              pickupHistory.note,
              pickupHistory.meta,
              String(pickupHistory.created_at),
              <number>pickupHistory.total_nfd
            )
        ),
        item.flag,
        '',
        item.shipment_group_ids,
        <string[]>item.product_type,
        item.weight,
        <string>item.client_id,
        <string>item.client_name,
        <string>item.vehicle_expectation,
        <string>item.pickup_date_expectation,
        <string>item.pickup_time_expectation,
        <number>item.estimate_koli,
        <number>item.estimasi_tonase,
        <number>item.total_nfd,
        <boolean>item.is_priority || false,
        <number>item.pickup_booster_amount || 0,
        <string>item.execution_platform || '',
        <string>item.pickup_date_estimation,
        <string>item.pickup_start_time,
        <string>item.pickup_end_time
      )
    })
  }

  public convertPickupDetailFromApi(result: AxiosResponse): PickupForceDetail {
    const {
      data: { data },
    } = result

    return new PickupForceDetail(
      data.actor_name,
      data.booking_id,
      data.courier_id,
      data.customer_role,
      data.is_dedicated,
      data.receiver_address,
      data.receiver_contact,
      data.receiver_name,
      data.shipment_id,
      data.task_id
    )
  }

  public convertSnapshotFromApi(result: AxiosResponse): PickupDetailSnapshot[] {
    const { data } = result
    return data.map((snap: PickupDetailSnapshotResponse) => {
      return new PickupDetailSnapshot(
        snap.snapshot_id,
        snap.shipment_id,
        snap.couriers.map((courier: CouriersResponse) => {
          return new Couriers(
            courier.courier_id,
            courier.latitude,
            courier.longitude,
            courier.distance,
            courier.created_at
          )
        }),
        snap.created_at
      )
    })
  }

  public convertNearbyCourierFromApi(result: AxiosResponse): NearbyCourier[] {
    const {
      data: { data },
    } = result

    if (!data) {
      return []
    }

    return data.map(
      (courier: Record<string, string | number | Record<string, boolean>>) => {
        const onDuty = courier.onduty as Record<string, boolean>

        return new NearbyCourier(
          Number(courier.courier_id),
          courier.courier_name as string,
          courier.courier_phone as string,
          courier.courier_pos as string,
          onDuty.onduty
        )
      }
    )
  }

  public convertPickupSummaryFromApi(result: AxiosResponse): any {
    const {
      data: { data },
    } = result
    if (!data) {
      return []
    }

    return new PickupSummary(
      data.crrsrc as number,
      data.crrnfd as number,
      data.dox as number,
      data.crrotw as number,
      data.crrpic as number,
      data.crrdon as number,
      data.all as number,
      data.crrque as number
    )
  }

  public convertCRRNFDExportFromApi(result: AxiosResponse): string {
    const {
      data: { url },
    } = result
    return url
  }

  public convertPickupAdditionalTimeFromApi(
    result: AxiosResponse
  ): PickupAdditionalTimes {
    const {
      data: { data, pagination },
    } = result

    return new PickupAdditionalTimes(
      Utils.toInstance(new Pagination(), pagination),
      data.map((item: Record<string, never>) => {
        return new PickupAdditionalTime(
          item.order_id,
          item.additional_time,
          item.courier_id,
          item.courier_name,
          item.start_time_pickup,
          item.end_time_pickup
        )
      })
    )
  }

  public convertPickupListFromApi(result: AxiosResponse): string {
    const {
      data: { url },
    } = result
    return url
  }

  public convertPickupCRROTWsFromApi(result: AxiosResponse): PickupCRROTWs {
    const {
      data: { data, pagination, summary },
    } = result
    return new PickupCRROTWs(
      Utils.toInstance(new Pagination(), pagination),
      data.map(
        (
          pickup: Record<
            string,
            string | number | boolean | Record<string, string | number>
          >
        ) => {
          const account = <Record<string, string | number>>pickup.account
          const pickupInfo = <Record<string, string | number>>pickup.pickup
          const courierPickupDetail = <Record<string, string | number>>(
            (pickup.courier_pickup_detail || pickup.courier_detail)
          )

          return new PickupCRROTW(
            <string>pickup.shipment_id,
            <string>pickup.group_id,
            <string>pickup.shipment_type,
            new PickupAccount(
              Number(account.account_id),
              <string>account.account_role
            ),
            pickupInfo
              ? new PickupInfo(
                  <string>pickupInfo.fullname,
                  <string>pickupInfo.address,
                  <string>pickupInfo.phone_number,
                  Number(pickupInfo.latitude),
                  Number(pickupInfo.longitude),
                  <string>pickupInfo.note,
                  <string>pickupInfo.country,
                  <string>pickupInfo.province,
                  <string>pickupInfo.district,
                  <string>pickupInfo.sub_district,
                  <string>pickupInfo.village
                )
              : null,
            <boolean>pickup.is_onepack,
            <string>pickup.product_type,
            Number(pickup.quantity),
            <string>pickup.created_at,
            <string>pickup.expired_at,
            <string>pickup.crrotw_at,
            <string>pickup.crrsrc_at,
            <string>pickup.last_updated,
            <string>pickup.do_origin_package_id,
            <string>pickup.booking_id,
            <string>pickup.task_id,
            <string>pickup.flag,
            <boolean>pickup.is_dedicated,
            <boolean>pickup.is_contacted,
            <boolean>pickup.can_contact,
            courierPickupDetail
              ? new CourierPickupDetail(
                  Number(courierPickupDetail.courier_id),
                  <string>(
                    (courierPickupDetail.courier_name ||
                      courierPickupDetail.fullname)
                  ),
                  <string>courierPickupDetail.courier_type_valid,
                  <string>courierPickupDetail.origin,
                  <string>courierPickupDetail.partner_name,
                  <string>courierPickupDetail.partner_type,
                  <string>courierPickupDetail.phone_number,
                  <string>courierPickupDetail.vehicle_plate,
                  <string>courierPickupDetail.vehicle_type
                )
              : undefined,
              <boolean>pickup.is_priority || false,
              <number>pickup.pickup_booster_amount || 0
          )
        }
      ),
      Utils.toInstance(new PickupCRROTWSummary(), summary)
    )
  }

  public convertPickupCRROTWV2ListFromApi(
    result: AxiosResponse
  ): PickupCRROTWV2List {
    const {
      data: { data, pagination },
    } = result
    const rows = Array.isArray(data) ? data : []

    return new PickupCRROTWV2List(
      Utils.toInstance(new Pagination(), pagination),
      rows.map(
        (
          row: Record<
            string,
            | string
            | number
            | boolean
            | Record<string, string | number>
            | unknown[]
          >
        ) => {
          const agentRaw = <Record<string, string> | undefined>row.agent
          const courierRaw = <Record<string, string | number> | undefined>(
            row.courier
          )
          const shipmentsRaw = Array.isArray(row.shipments)
            ? row.shipments
            : []

          return new PickupCRROTWV2Group(
            Number(row.task_group_id ?? 0),
            String(row.shipper_hash ?? ''),
            String(row.sender_name ?? ''),
            String(row.sender_address ?? ''),
            String(row.sender_phone ?? ''),
            Number(row.total_shipments ?? 0),
            Number(row.total_quantity ?? 0),
            String(row.shipment_type ?? ''),
            new PickupCRROTWV2Agent(
              String(agentRaw?.code ?? ''),
              String(agentRaw?.name ?? '')
            ),
            new PickupCRROTWV2Courier(
              Number(courierRaw?.id ?? 0),
              String(courierRaw?.name ?? ''),
              courierRaw && Array.isArray(courierRaw.drivers) && courierRaw.drivers.length > 1
                ? ''
                : String(courierRaw?.phone_number ?? ''),
              String(courierRaw?.type ?? ''),
              courierRaw && Array.isArray(courierRaw.drivers)
                ? (courierRaw.drivers as Array<Record<string, unknown>>).map(
                    (d: Record<string, unknown>) =>
                      new PickupCRROTWV2Driver(
                        Number(d.id ?? 0),
                        String(d.name ?? ''),
                        String(d.phone_number ?? '')
                      )
                  )
                : undefined
            ),
            String(row.created_at ?? ''),
            String(row.expired_at ?? ''),
            Number(row.duration_shpcrt_crrotw_minute ?? 0),
            Number(row.duration_crrsrc_crrotw_minute ?? 0),
            shipmentsRaw.map((s: unknown) => {
              const line = s as Record<string, string | number>
              return new PickupCRROTWV2ShipmentDetail(
                String(line.shipment_id ?? ''),
                String(line.product_type ?? ''),
                Number(line.quantity ?? 0)
              )
            }),
            Number(row.latitude ?? 0),
            Number(row.longitude ?? 0),
            String(row.execution_platform ?? row.executionPlatform ?? ''),
            Boolean(row.is_priority ?? false),
          )
        }
      )
    )
  }

  public convertPickupCRROTWV2SummaryFromApi(
    result: AxiosResponse
  ): PickupCRROTWV2Summary {
    const inner = result.data?.data as
      | {
          total_groups?: number
          buckets?: Partial<Record<string, number>>
        }
      | undefined

    const rawBuckets = inner?.buckets ?? {}
    const normalized = new PickupCRROTWV2DurationBuckets(
      rawBuckets as Partial<Record<PickupCRROTWV2BucketKey, number>>
    )

    return new PickupCRROTWV2Summary(
      Number(inner?.total_groups ?? 0),
      normalized
    )
  }

  public convertCRROTWExportFromApi(result: AxiosResponse): string {
    const {
      data: { url },
    } = result
    return url
  }

  public convertDashboardPickupTransferFromApi(
    result: AxiosResponse
  ): DashboardPickupTransferOutcome[] {
    const raw = result.data?.data ?? result.data
    const arr = Array.isArray(raw) ? raw : []
    return arr.map(
      (row: { task_group_id?: number; status?: boolean }) =>
        new DashboardPickupTransferOutcome(
          Number(row.task_group_id ?? 0),
          Boolean(row.status)
        )
    )
  }

  public convertCRRQUEFromApi(result: AxiosResponse): PickupCRRQUEs {
    const {
      data: { data, pagination, summary },
    } = result

    return new PickupCRRQUEs(
      data.map(
        (
          pickup: Record<
            string,
            | string
            | number
            | boolean
            | Record<string, string | number | boolean>
          >
        ) => {
          const customer = <Record<string, string | number>>pickup.customer
          const pos = <Record<string, string | number>>pickup.pos
          const pickupData = <Record<string, string | number>>pickup.pickup

          return new PickupCRRQUE(
            <string>pickup.shipment_id,
            <string>pickup.shipment_type,
            new PickupCRRQUECustomer(
              <string>customer.name,
              <string>customer.address,
              <string>customer.phone_number
            ),
            new PickupCRRQUEPOS(
              <string>pos.name,
              <string>pos.phone,
              <number>pos.agent_id,
              <string>pos.agent_code,
              <string>pos.address
            ),
            new PickupCRRQUEPickup(
              <number>pickupData.latitude,
              <number>pickupData.longitude
            ),
            <boolean>pickup.is_contacted,
            <string>pickup.created_at,
            <string>pickup.expired_at,
            <string>pickup.crrque_at,
            <string>pickup.crrsrc_at,
            <string>pickup.flag,
            <boolean>pickup.can_contact
          )
        }
      ),
      Utils.toInstance(new Pagination(), pagination),
      Utils.toInstance(new PickupCRROTWSummary(), summary)
    )
  }

  convertCRRQUEExportFromApi(result: AxiosResponse): string {
    const {
      data: { url },
    } = result

    return url
  }

  convertCRRNFDSummaryFromApi(result: AxiosResponse): PickupCRRNFDSummary {
    const { data } = result

    return new PickupCRRNFDSummary(
      data.one,
      data.two,
      data.three,
      data.four,
      data.five,
      data.more_than_five
    )
  }

  convertPickupNfdListFromApi(
    result: AxiosResponse
  ): PickupNfdList {
    const {
      data: { data, pagination },
    } = result

    const rows = (data || []).map(
      (item: Record<string, unknown>) => {
        const lc = item.last_courier as
          | Record<string, string | number | null | undefined>
          | null
          | undefined
        const lastCourier =
          lc &&
          (lc.courier_id != null ||
            (lc.courier_name != null && String(lc.courier_name) !== ''))
            ? new PickupNfdLastCourier(
                String(lc.courier_id ?? ''),
                String(lc.courier_name ?? ''),
                String(lc.courier_phone ?? ''),
                String(lc.courier_type ?? ''),
                String(lc.agent_name ?? ''),
                String(lc.agent_code ?? '')
              )
            : null
        return new PickupNfdGroup(
          String(item.shipper_hash ?? ''),
          String(item.sender_name ?? ''),
          String(item.sender_phone ?? ''),
          String(item.sender_address ?? ''),
          Number(item.total_shipments ?? 0),
          Number(item.total_quantity ?? 0),
          String(item.shipment_type ?? ''),
          String(item.crrnfd_at ?? ''),
          Number(item.crrnfd_duration_minute ?? 0),
          Number(item.crrnfd_iteration ?? 0),
          item.client_id != null && item.client_id !== ''
            ? String(item.client_id)
            : null,
          item.client_name != null ? String(item.client_name) : undefined,
          lastCourier,
          item.last_cancel_reason != null
            ? String(item.last_cancel_reason)
            : '',
          Number(item.longitude ?? 0),
          Number(item.latitude ?? 0),
          Boolean(item.is_priority ?? false),
        )
      }
    )

    return new PickupNfdList(
      Utils.toInstance(new Pagination(), pagination || {}),
      rows
    )
  }

  convertPickupNfdShipperHashDetailFromApi(
    result: AxiosResponse,
    shipperHash: string
  ): PickupNfdShipperHashDetail {
    const root = result.data as Record<string, unknown>
    let payload = root?.data as Record<string, unknown> | undefined
    let paginationRaw: unknown = root?.pagination

    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const shipmentsTest = payload.shipments
      if (!Array.isArray(shipmentsTest) && payload.data) {
        paginationRaw =
          (payload as { pagination?: unknown }).pagination ?? paginationRaw
        payload = payload.data as Record<string, unknown>
      }
    }

    const p = (payload || {}) as Record<string, unknown>
    const lc = p.last_courier as
      | Record<string, string | number | null | undefined>
      | null
      | undefined
    const lastCourier =
      lc &&
      (lc.courier_id != null ||
        (lc.courier_name != null && String(lc.courier_name) !== ''))
        ? new PickupNfdLastCourier(
            String(lc.courier_id ?? ''),
            String(lc.courier_name ?? ''),
            String(lc.courier_phone ?? ''),
            String(lc.courier_type ?? ''),
            String(lc.agent_name ?? ''),
            String(lc.agent_code ?? '')
          )
        : null

    const shipmentsRaw = Array.isArray(p.shipments) ? p.shipments : []
    const shipments = shipmentsRaw.map(
      (item: Record<string, unknown>) =>
        new PickupNfdDetailShipment(
          String(item.shipment_id ?? ''),
          String(item.product_type ?? ''),
          Number(item.total_quantity ?? 0),
          String(item.crrnfd_at ?? ''),
          Number(item.crrnfd_duration_minute ?? 0),
          Number(item.crrnfd_iteration ?? 0)
        )
    )

    const pagination = Utils.toInstance(
      new Pagination(),
      JSON.stringify(
        paginationRaw &&
          typeof paginationRaw === 'object' &&
          !Array.isArray(paginationRaw)
          ? paginationRaw
          : {}
      )
    )

    return new PickupNfdShipperHashDetail(
      String(shipperHash || '').trim(),
      String(p.sender_name ?? ''),
      String(p.sender_phone ?? ''),
      String(p.sender_address ?? ''),
      p.client_id != null && String(p.client_id) !== ''
        ? String(p.client_id)
        : null,
      p.client_name != null ? String(p.client_name) : '',
      lastCourier,
      p.last_cancel_reason != null ? String(p.last_cancel_reason) : '',
      shipments,
      pagination,
      Number(p.longitude ?? ''),
      Number(p.latitude ?? ''),
      Boolean(p.is_priority ?? false)
    )
  }

  convertPickupNfdSummaryFromApi(
    result: AxiosResponse
  ): PickupNfdSummary {
    const {
      data: { data },
    } = result

    const buckets = new PickupCRROTWV2DurationBuckets(data?.buckets)

    return new PickupNfdSummary(
      Number(data?.total_groups ?? 0),
      buckets
    )
  }

  convertPickupShipperHashOutcomesFromApi(
    result: AxiosResponse
  ): PickupShipperHashOutcome[] {
    const raw = result.data?.data?.results
    const list = Array.isArray(raw) ? raw : []
    return list.map(
      (r: Record<string, string | boolean | null | undefined>) =>
        new PickupShipperHashOutcome(
          String(r.shipper_hash ?? ''),
          Boolean(r.is_success),
          r.message != null ? String(r.message) : undefined
        )
    )
  }

  convertBulkManualAssignV2OutcomesFromApi(
    result: AxiosResponse
  ): PickupShipperHashOutcome[] {
    const raw = result.data?.data
    const list = Array.isArray(raw) ? raw : []
    return list.map(
      (r: Record<string, string | boolean | null | undefined>) =>
        new PickupShipperHashOutcome(
          String(r.id ?? r.shipper_hash ?? ''),
          Boolean(r.is_success),
          r.message != null ? String(r.message) : undefined
        )
    )
  }

  convertAssignPickupBulkHistoryListFromApi(
    result: AxiosResponse
  ): AssignPickupBulkHistoryList {
    const {
      data: { data, pagination },
    } = result

    const paginationData = new AssignPickupBulkHistoryPagination({
      page: pagination.page,
      perPage: pagination.per_page,
      totalItem: pagination.total_item,
      totalAssign: pagination.total_assign,
      totalProcess: pagination.total_process,
      totalSuccess: pagination.total_success,
      totalFailed: pagination.total_failed
    })

    const rows = (data || []).map(
      (item: Record<string, unknown>) => {
        return new AssignPickupBulkHistoryItem(
          Number(item.id ?? 0),
          String(item.status ?? ''),
          String(item.created_at ?? ''),
          String(item.updated_at ?? ''),
          String(item.shipper_hash ?? ''),
          String(item.seller_name ?? ''),
          Number(item.total_shipment ?? 0),
          String(item.phone_number ?? '')
        )
      }
    )

    return new AssignPickupBulkHistoryList(
      paginationData,
      rows
    )
  }

  convertManualAssignPickupFromApi(result: AxiosResponse): string {
    const {
      data: { data },
    } = result

    return data.url
  }

  convertPickupCorporatesFromApi(result: AxiosResponse): PickupCorporates {
    const {
      data: { data, pagination },
    } = result

    const pickupData = data || []
    const paginationData = JSON.stringify(pagination)

    return new PickupCorporates({
      data: pickupData.map((item: any) => {
        const courierPickupDetail = item.courier_pickup_detail

        return new PickupCorporate({
          shipmentId: item.shipment_id,
          groupId: item.group_id,
          shipmentType: item.shipment_type,
          account: new PickupAccount(
            item.account.account_id,
            item.account.account_role
          ),
          pickup: new PickupInfo(
            item.pickup.fullname,
            item.pickup.address,
            item.pickup.phone_number,
            item.pickup.latitude,
            item.pickup.longitude,
            item.pickup.note,
            item.pickup.country,
            item.pickup.province,
            item.pickup.district,
            item.pickup.sub_district,
            item.pickup.village
          ),
          isOnepack: item.is_onepack,
          productType: item.product_type,
          quantity: item.quantity,
          weight: item.weight,
          createdAt: item.created_at,
          expirateAt: item.expired_at,
          history: item.history
            ? new PickupHistory(
                item.history.history_id,
                item.history.history_type,
                item.history.status_id,
                item.history.status,
                item.history.location,
                item.history.city,
                item.history.actor_id,
                item.history.actor_role,
                item.history.actor_name,
                item.history.reason_id,
                item.history.reason,
                item.history.relation_id,
                item.history.relation,
                item.history.person_name,
                item.history.note,
                item.history.meta,
                item.history.created_at,
                item.history.total_nfd || 0
              )
            : ({} as PickupHistory),
          flag: item.flag,
          doOriginPackageId: item.do_origin_package_id,
          bookingId: item.booking_id,
          taskId: item.task_id,
          isDedicated: item.is_dedicated,
          clientId: item.client_id,
          clientName: item.client_name,
          totalNfd: item.history.total_nfd || item.total_nfd,
          estimateKoli: Number(item.estimate_koli),
          estimasiTonasi: Number(item.estimasi_tonase),
          vehicleExpectation: item.transport_type || item.vehicle_expectation,
          pickupDateExpectation: item.pickup_date_expectation,
          pickupTimeExpectation: item.pickup_time_expectation,
          couerierPickupDetail: courierPickupDetail
            ? new CourierPickupDetail(
                Number(courierPickupDetail.courier_id),
                <string>(
                  (courierPickupDetail.courier_name ||
                    courierPickupDetail.fullname)
                ),
                <string>courierPickupDetail.courier_type_valid,
                <string>courierPickupDetail.origin,
                <string>courierPickupDetail.partner_name,
                <string>courierPickupDetail.partner_type,
                <string>courierPickupDetail.phone_number,
                <string>courierPickupDetail.vehicle_plate,
                <string>courierPickupDetail.vehicle_type
              )
            : undefined,
        })
      }),
      pagination: Utils.toInstance(new Pagination(), paginationData),
    })
  }

  convertPickupCorporatesFromApiV2(result: AxiosResponse): PickupCorporatesV2 {
    const {
      data: { data, pagination },
    } = result

    const pickupData = data || []
    const paginationData = JSON.stringify(pagination)

    return new PickupCorporatesV2({
      data: pickupData.map((item: any) => {
        const courierPickupDetail = item.courier_pickup_detail

        return new PickupCorporateV2({
          shipmentId: item.shipment_id,
          clientId: item.client_id,
          clientName: item.client_name,
          estimatePickupDate: item.pickup_date_expectation,
          estimatePickupTime: item.pickup_time_expectation,
          estimateTotalKoli: item.estimate_koli || 0,
          estimateTotalTonase: item.estimasi_tonase || 0,
          expiretAt: item.expired_at,
          createdAt: item.created_at,
          quantity: item.quantity,
          specialHandling: item.special_handling,
          totalNfd: item.total_nfd,
          transportType: item.transport_type,
          weight: item.weight,
          executionPlatform:
            item.execution_platform || item.execution?.platform || '',
          pickup: new PickupInfo(
            item.pickup.fullname,
            item.pickup.address,
            item.pickup.phone_number,
            item.pickup.latitude,
            item.pickup.longitude,
            item.pickup.note,
            item.pickup.country,
            item.pickup.province,
            item.pickup.district,
            item.pickup.sub_district,
            item.pickup.village
          ),
          couerierPickupDetail: courierPickupDetail
            ? new CourierPickupDetail(
                Number(courierPickupDetail.courier_id),
                <string>(
                  (courierPickupDetail.courier_name ||
                    courierPickupDetail.fullname)
                ),
                <string>courierPickupDetail.courier_type_valid,
                <string>courierPickupDetail.origin,
                <string>courierPickupDetail.partner_name,
                <string>courierPickupDetail.partner_type,
                <string>courierPickupDetail.phone_number,
                <string>courierPickupDetail.vehicle_plate,
                <string>courierPickupDetail.vehicle_type
              )
            : undefined,
          history: item.history
            ? new PickupHistory(
                item.history.history_id,
                item.history.history_type,
                item.history.status_id,
                item.history.status,
                item.history.location,
                item.history.city,
                item.history.actor_id,
                item.history.actor_role,
                item.history.actor_name,
                item.history.reason_id,
                item.history.reason,
                item.history.relation_id,
                item.history.relation,
                item.history.person_name,
                item.history.note,
                item.history.meta,
                item.history.created_at,
                item.history.total_nfd || 0
              )
            : ({} as PickupHistory),
        })
      }),
      pagination: Utils.toInstance(new Pagination(), paginationData),
    })
  }


  convertPickupCorporateSummaryFromApi(
    result: AxiosResponse
  ): PickupCorporateSummary {
    const { data } = result

    return new PickupCorporateSummary({
      one: data.one,
      two: data.two,
      three: data.three,
      four: data.four,
      five: data.five,
      moreThanFive: data.more_than_five,
    })
  }

  public convertExportPickupFromApi(result: AxiosResponse): Report {
    const { data: {bulk_download} } = result

    return new Report({
      createdAt: bulk_download.created_at,
      id: bulk_download.id,
      status: bulk_download.status,
      type: bulk_download.type,
    })
  }

  public convertAdminContactShowFromApi(result: AxiosResponse): AdminContactShow {
    const { data } = result;

    return new AdminContactShow({
      resource: data.resource,
      resourceId: data.resource_id,
      field: data.field,
      value: data.value,
    });
  }

  public convertSyncPickupCorporateFromApi(result: AxiosResponse<any>) {
    const { data } = result;

    return <string>data?.latest_synced_at || '';
  }

  convertPickupCorporatesNfdDeadlineSummaryFromApi(result: AxiosResponse): PickupCorporateNfdDeadlineSummary {
    const {
      data: { data },
    } = result

    return new PickupCorporateNfdDeadlineSummary({
      totalInBuckets: data.total_in_buckets,
      buckets: new PickupCorporatBuckets({
        threeHoursLeft: data.buckets.three_hours_left,
        twoHoursLeft: data.buckets.two_hours_left,
        oneHourLeft: data.buckets.one_hour_left,
        lateOneHour: data.buckets.late_one_hour,
        lateTwoHours: data.buckets.late_two_hours,
        lateOverThreeHours: data.buckets.late_over_three_hours
      })
    })
  }

  convertExportCorporateFromApi(result: AxiosResponse): Report {
    const { data: {bulk_download} } = result

    return new Report({
      createdAt: bulk_download.created_at,
      id: bulk_download.id,
      status: bulk_download.status,
      type: bulk_download.type,
    })
  }
}
